/**
 * MER-612 + MER-611: say on screen what Period and Range are, and what the
 * page is actually doing with them right now.
 *
 * Zay asked twice on 2026-09-02: "what's the difference between period and
 * range? That would be a good thing to explain." And: "we just need to know
 * what the default is ... it might be helpful having somewhere on here that
 * denotes what the default selection is."
 *
 * Two facts the bar never stated:
 *
 * 1. They are not two settings on one axis. PERIOD picks the bucket size (the
 *    monthly or weekly rows in kpi_summary). RANGE picks the window of time.
 *    A tile can be monthly buckets over a year or weekly buckets over 30 days;
 *    neither control implies the other.
 *
 * 2. THERE IS NO DASHBOARD-WIDE DEFAULT RANGE. The answer given in the room
 *    was "monthly" for period, and the meeting notes recorded "default range:
 *    year-to-date". That is not true and never has been on this build:
 *    MER-421 removed the silent ytd promotion, so with Range on Default every
 *    tile keeps the window it was authored with. A label reading "year-to-
 *    date" would have been wrong, and most wrong on the disposal tiles Zay was
 *    looking at, which are authored last_30_days.
 *
 * Period's Default is different again, and the difference matters: a tile that
 * DECLARES a grain keeps it (MER-497), and a tile that declares none on a
 * period-aware source receives the global value (MER-362). So Default means
 * "each tile keeps the grain it was built with", not "no grain".
 *
 * Copy lives here rather than inline in the bar so node:test can pin the
 * sentences, and so the honest-default wording cannot drift back towards the
 * version that misled the room.
 *
 * Pure .mjs, no React: filterLegend.d.mts types the TS caller.
 */

/**
 * The standing explanation. One line, because it sits above every tab and a
 * paragraph there would be read once and then skipped forever.
 */
export const FILTER_LEGEND =
  "Period sets the size of the bucket each number is grouped into. " +
  "Range sets the window of time on screen.";

/**
 * What the Period selector is doing to the tiles right now.
 *
 * @param {{ explicit?: boolean, label?: string }} state
 *   `explicit` is GlobalFilters.periodExplicit: true only when the URL carries
 *   a real user selection. `label` is the selected period's display label.
 * @returns {string}
 */
export function periodLegend(state) {
  const explicit = state?.explicit === true;
  const label = typeof state?.label === "string" ? state.label.trim() : "";
  if (!explicit || !label) {
    return "Period: Default, so each tile keeps the grain it was built with.";
  }
  return `Period: ${label}, so every tile that has a grain is grouped that way.`;
}

/**
 * What the Range selector is doing to the tiles right now.
 *
 * The Default sentence is the MER-611 correction and is the whole point of
 * this module: it states outright that there is no dashboard-wide default
 * range, so nobody reads the blank Default chip as a hidden year-to-date.
 *
 * @param {{ explicit?: boolean, label?: string, month?: string | null }} state
 *   `explicit` is GlobalFilters.rangeExplicit (a PRESET selection). `month` is
 *   the display label of an explicit calendar month (MER-692), which rides the
 *   same URL slot and lights no preset chip.
 * @returns {string}
 */
export function rangeLegend(state) {
  const month = typeof state?.month === "string" ? state.month.trim() : "";
  if (month) {
    return `Range: ${month}, applied to every tile that follows the page range.`;
  }
  const explicit = state?.explicit === true;
  const label = typeof state?.label === "string" ? state.label.trim() : "";
  if (!explicit || !label) {
    return (
      "Range: Default, so each tile keeps its own window. " +
      "There is no dashboard-wide default range."
    );
  }
  return `Range: ${label}, applied to every tile that follows the page range.`;
}
